import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { Textarea } from './Input'

export interface PropsModal {
  terbuka: boolean
  onTutup: () => void
  judul: ReactNode
  deskripsi?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  lebar?: string
}

/**
 * Dialog aksesibel: role="dialog", aria-modal, fokus awal di dalam dialog,
 * jebakan Tab sederhana, dan penutupan dengan tombol Escape.
 */
export function Modal({
  terbuka,
  onTutup,
  judul,
  deskripsi,
  children,
  footer,
  lebar = 'max-w-2xl',
}: PropsModal) {
  const idJudul = useId()
  const idDeskripsi = useId()
  const refDialog = useRef<HTMLDivElement | null>(null)
  const refFokusSebelumnya = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!terbuka) return
    refFokusSebelumnya.current = document.activeElement as HTMLElement | null
    const simpul = refDialog.current
    const pertama = simpul?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), select, textarea, button, [href], [tabindex]:not([tabindex="-1"])',
    )
    ;(pertama ?? simpul)?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onTutup()
        return
      }
      if (e.key === 'Tab' && simpul) {
        const dapatFokus = Array.from(
          simpul.querySelectorAll<HTMLElement>(
            'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => el.offsetParent !== null)
        if (dapatFokus.length === 0) return
        const pertamaEl = dapatFokus[0]
        const terakhirEl = dapatFokus[dapatFokus.length - 1]
        if (e.shiftKey && document.activeElement === pertamaEl) {
          e.preventDefault()
          terakhirEl.focus()
        } else if (!e.shiftKey && document.activeElement === terakhirEl) {
          e.preventDefault()
          pertamaEl.focus()
        }
      }
    }

    document.addEventListener('keydown', onKey)
    const overflowSebelumnya = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflowSebelumnya
      refFokusSebelumnya.current?.focus?.()
    }
  }, [terbuka, onTutup])

  if (!terbuka) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-netral-900/50 p-4 sm:items-center">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        onClick={onTutup}
      />
      <div
        ref={refDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idJudul}
        aria-describedby={deskripsi ? idDeskripsi : undefined}
        tabIndex={-1}
        className={`relative z-10 w-full ${lebar} rounded-xl border border-netral-200 bg-white shadow-xl`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-netral-200 px-5 py-3.5">
          <div>
            <h2 id={idJudul} className="text-base font-semibold text-netral-900">
              {judul}
            </h2>
            {deskripsi ? (
              <p id={idDeskripsi} className="mt-0.5 text-sm text-netral-500">
                {deskripsi}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onTutup}
            aria-label="Tutup dialog"
            className="rounded-md p-1 text-netral-500 hover:bg-netral-100 hover:text-netral-800"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-netral-200 px-5 py-3.5">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  )
}

export interface PropsKonfirmasi {
  terbuka: boolean
  onTutup: () => void
  onKonfirmasi: (alasan: string) => void | Promise<void>
  judul: string
  pesan: ReactNode
  labelKonfirmasi?: string
  berbahaya?: boolean
  memuat?: boolean
  /** Bila diisi, pengguna WAJIB menuliskan alasan (mis. pembalikan jurnal). */
  labelAlasan?: string
  minimalAlasan?: number
}

/** Dialog konfirmasi untuk tindakan berisiko (posting, pembatalan, pembalikan). */
export function ConfirmDialog({
  terbuka,
  onTutup,
  onKonfirmasi,
  judul,
  pesan,
  labelKonfirmasi = 'Lanjutkan',
  berbahaya = true,
  memuat = false,
  labelAlasan,
  minimalAlasan = 0,
}: PropsKonfirmasi) {
  const [alasan, setAlasan] = useState('')

  useEffect(() => {
    if (terbuka) setAlasan('')
  }, [terbuka])

  const kurang = labelAlasan !== undefined && alasan.trim().length < minimalAlasan

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul={judul}
      lebar="max-w-lg"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button
            varian={berbahaya ? 'bahaya' : 'utama'}
            memuat={memuat}
            onClick={() => void onKonfirmasi(alasan)}
          >
            {labelKonfirmasi}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-netral-700">
        <div>{pesan}</div>
        {labelAlasan ? (
          <Textarea
            label={labelAlasan}
            value={alasan}
            onChange={(e) => setAlasan(e.target.value)}
            rows={3}
            autoFocus
            bantuan={
              minimalAlasan > 0
                ? `Minimal ${minimalAlasan} karakter. ${kurang ? `Sisa ${minimalAlasan - alasan.trim().length} karakter.` : 'Sudah cukup.'}`
                : undefined
            }
            galat={kurang && alasan.length > 0 ? 'Alasan belum memenuhi panjang minimal.' : null}
          />
        ) : null}
      </div>
    </Modal>
  )
}
